import { TGlobalStores } from '@/editor/stores';
import EventBus from '@/editor/utils/EventBus';
import {
  EShortcutId,
  EShortcutPlatform,
  EShortcutScope,
  formatShortcutKeys,
  getCurrentPlatform,
  getShortcutKeys,
  SHORTCUT_MAP
} from '@/editor/controller/keyboardController/config';
import { isString } from '@/utils/types/assert';
import { EOperateMode } from '@/editor/types';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';

interface IParsedKey {
  key: string;
  modifiers: ModifierState;
  /** Key 构成的优先级：mod+shift+key = 3, mod+key = 2, shift+key = 1.5, key = 1 */
  priority: number;
}

interface ModifierState {
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
  mod: boolean; // 跨平台修饰键：Mac = meta, Windows/Linux = ctrl
}

interface IParsedShortcut {
  id: EShortcutId;
  parsedKeys: IParsedKey[];
  originalKeys: string[];
  callback: (event: KeyboardEvent, setCaptured: () => void) => void;
  /** 允许事件继续触发其他匹配的快捷键 */
  noCapture: boolean;
  /** 注册后是否阻止默认行为（快捷键禁用/启用都会） */
  preventDefault?: boolean;
  disabled: boolean;
  scope: EShortcutScope[];
  /** 所有 parsedKeys 中的最高优先级 */
  priority: number;
}

interface IRegisterData {
  id: EShortcutId;
  callback: (event: KeyboardEvent, setCaptured: () => void) => void;
  disabled?: boolean;
}

export enum EKeyboardEventName {
  NOTIFY_SHORTCUTS_UPDATE
}

type TKeyboardEvent = {
  [EKeyboardEventName.NOTIFY_SHORTCUTS_UPDATE]: () => void;
};

const DRAWING_MODE = new Set([
  EOperateMode.DrawDoorWindow,
  EOperateMode.DrawFurniture,
  EOperateMode.DrawSeparateWall,
  EOperateMode.DrawWall,
  EOperateMode.DrawStruct,
  EOperateMode.DrawRectWall
]);

export class KeyboardController {
  #gs: TGlobalStores;
  #shortcutMap: Map<EShortcutId, IParsedShortcut>;
  #shortcuts: IParsedShortcut[];
  #eventBus: EventBus<TKeyboardEvent>;
  #platform: EShortcutPlatform;

  /** 当前处于 MODAL 作用域的弹窗数量，见 {@link setInModal} */
  #modalCount: number = 0;
  #inEditor: boolean = false;
  #inOverlay: boolean = false;

  constructor(gs: TGlobalStores) {
    this.#gs = gs;
    this.#platform = getCurrentPlatform();
    this.#shortcutMap = new Map();
    this.#shortcuts = [];
    this.#eventBus = new EventBus();
    this.#initListener();
  }

  /** 根据状态更新快捷键作用域 */
  private _updateScope = () => {
    // Overlay > Modal > Drawing > Editor > Global
    if (this.#inOverlay) {
      this.#gs.keyboardStore.setShortcutScope(EShortcutScope.OVERLAY);
    } else if (this.#modalCount > 0) {
      this.#gs.keyboardStore.setShortcutScope(EShortcutScope.MODAL);
    } else if (DRAWING_MODE.has(this.#gs.operateInfo.mode)) {
      this.#gs.keyboardStore.setShortcutScope(EShortcutScope.DRAWING);
    } else if (this.#inEditor) {
      this.#gs.keyboardStore.setShortcutScope(EShortcutScope.EDITOR);
    } else {
      this.#gs.keyboardStore.setShortcutScope(EShortcutScope.GLOBAL);
    }
  };

  /**
   * 进入 MODAL 作用域，返回退出函数。
   *
   * 用计数而非布尔标志：页面级弹窗与全局 confirm 会叠加（例如 CAD 导入弹窗里
   * 报错再弹一个提示），布尔标志下先关闭的那个会把仍然开着的弹窗的快捷键屏蔽一起解除。
   */
  public setInModal = () => {
    this.#modalCount += 1;
    this._updateScope();
    return () => {
      this.#modalCount = Math.max(0, this.#modalCount - 1);
      this._updateScope();
    };
  };

  public setInOverlay = () => {
    this.#inOverlay = true;
    this.#gs.keyboardStore.clearAllKeys();
    this._updateScope();
    return () => {
      this.#inOverlay = false;
      this.#gs.keyboardStore.clearAllKeys();
      this._updateScope();
    };
  };

  public setInEditor = (value: boolean) => {
    this.#inEditor = value;
    this._updateScope();
  };

  public get shortcuts() {
    return this.#shortcuts;
  }

  /** 添加事件监听 */
  #initListener() {
    // 确保窗口获得焦点以接收键盘事件
    window.focus();

    // 监听窗口失焦事件，防止快捷键状态错乱
    window.addEventListener('blur', this._handleWindowBlur);

    // 监听键盘事件
    window.document.addEventListener('keydown', this._handleKeyDown);
    window.document.addEventListener('keyup', this._handleKeyUp);

    // 监听鼠标移动事件，保证 modifier key 状态同步
    window.document.addEventListener('mousemove', this.#gs.keyboardStore.updateModifierKeys);

    // 监听操作模式变化，更新快捷键作用域
    editorEvent.on(EditorEventName.OPERATE_MODE_CHANGE, this._updateScope);
  }

  /** 移除事件监听  */
  #removeListener() {
    window.removeEventListener('blur', this._handleWindowBlur);
    window.document.removeEventListener('keydown', this._handleKeyDown);
    window.document.removeEventListener('keyup', this._handleKeyUp);
    window.document.removeEventListener('mousemove', this.#gs.keyboardStore.updateModifierKeys);
    editorEvent.off(EditorEventName.OPERATE_MODE_CHANGE, this._updateScope);
  }

  /**
   * 注册快捷键
   * @returns 取消注册函数
   */
  public register({ id, callback, disabled = false }: IRegisterData): () => void {
    const config = SHORTCUT_MAP[id];

    if (!config) {
      console.warn(`[KeyboardController] 未知快捷键 ID: "${id}"`);
      return () => {};
    }

    const originalKeys = getShortcutKeys(id, this.#platform);
    const parsedKeys = originalKeys.map((k) => this._parseKey(k));
    const scope = config.scope ?? EShortcutScope.GLOBAL;
    const priority = Math.max(...parsedKeys.map((pk) => pk.priority));
    const noCapture = config.noCapture ?? false;
    const preventDefault = config.preventDefault ?? false;

    this.#setShortcutMap(id, {
      id,
      parsedKeys,
      originalKeys,
      callback,
      disabled,
      scope,
      priority,
      preventDefault,
      noCapture
    });

    return () => this.unregister(id);
  }

  /**
   * 取消注册快捷键
   */
  public unregister(id: EShortcutId) {
    this.#setShortcutMap(id);
  }

  #setShortcutMap(id: EShortcutId, parsedConfig?: IParsedShortcut) {
    if (parsedConfig) {
      this.#shortcutMap.set(id, parsedConfig);
    } else {
      this.#shortcutMap.delete(id);
    }
    this.#shortcuts = Array.from(this.#shortcutMap.values());
    this.emitEvent(EKeyboardEventName.NOTIFY_SHORTCUTS_UPDATE);
  }

  /**
   * 更新快捷键配置（如 disabled 状态）
   * @returns 是否有更新发生
   */
  public update(id: EShortcutId, updates: Partial<Omit<IRegisterData, 'id'>>) {
    const shortcut = this.#shortcutMap.get(id);

    if (!shortcut) {
      return false;
    }

    let updated = false;

    if (updates.disabled !== undefined) {
      shortcut.disabled = updates.disabled;
      updated = true;
    }

    if (updates.callback !== undefined) {
      shortcut.callback = updates.callback;
      updated = true;
    }

    if (updated) {
      this.#setShortcutMap(id, shortcut);
    }

    return updated;
  }

  private get _isApple(): boolean {
    return this.#platform === EShortcutPlatform.MAC;
  }

  isShortcutAvailable(id: EShortcutId): boolean {
    const shortcut = this.#shortcutMap.get(id);
    if (!shortcut) return false;
    return !shortcut.disabled;
  }

  getShortcutLabel(id: EShortcutId): string | null {
    const shortcut = this.#shortcutMap.get(id);
    if (!shortcut) return null;

    return formatShortcutKeys(shortcut.originalKeys);
  }

  public getShortcutStatus(id: EShortcutId): { available: boolean; label: string | null } {
    const shortcut = this.#shortcutMap.get(id);
    if (!shortcut) {
      return { available: false, label: null };
    }

    return {
      available: !shortcut.disabled,
      label: formatShortcutKeys(shortcut.originalKeys)
    };
  }

  private _handleKeyDown = (event: KeyboardEvent) => {
    if (!isString(event.key)) {
      return;
    }

    const key = event.key.toLowerCase();

    const isModifier = this._isModifierKey(key);

    if (this.#isInputElement(event.target)) {
      // 输入框内不响应除了 Escape 键
      if (key !== 'escape') {
        return;
      }
    }

    // 更新按键状态
    if (!isModifier) {
      if (event.ctrlKey === false && event.altKey === false && event.shiftKey === false && event.metaKey === false) {
        this.#gs.keyboardStore.pressKey(key);
      }
    } else {
      this.#gs.keyboardStore.pressKey(key);
    }

    // 匹配并执行快捷键
    this._matchAndExecute(event);
  };

  private _isModifierKey(key: string): boolean {
    return ['control', 'ctrl', 'alt', 'shift', 'meta'].includes(key?.toLowerCase());
  }

  private _handleKeyUp = (event: KeyboardEvent) => {
    if (!isString(event.key)) {
      return;
    }

    const key = event.key.toLowerCase();
    this.#gs.keyboardStore.clearKey(key);
  };

  /**
   * 窗口失焦时清除所有按键状态，防止重新获得焦点后状态错乱导致快捷键失效的问题
   */
  private _handleWindowBlur = () => {
    this.#gs.keyboardStore.clearAllKeys();
  };

  private _matchAndExecute(event: KeyboardEvent) {
    const key = event.key.toLowerCase();

    // 收集所有匹配的快捷键
    const matches: { shortcut: IParsedShortcut; parsed: IParsedKey }[] = [];

    for (const shortcut of this.#shortcuts) {
      for (const parsed of shortcut.parsedKeys) {
        if (this._matchParsedKey(parsed, key, event)) {
          if (shortcut.preventDefault) {
            event.preventDefault();
          }
          if (shortcut.disabled || !shortcut.scope.includes(this.#gs.keyboardStore.shortcutScope)) {
            break;
          }
          matches.push({ shortcut, parsed });
          break; // 同一快捷键配置只匹配一次
        }
      }
    }

    if (matches.length === 0) return;

    // 按优先级排序：
    // 1. 当前 scope > global scope
    // 2. 同 scope 内，按 priority 权重排序（高优先）
    matches.sort((a, b) => {
      // Scope 优先级
      const scopeA = a.shortcut.scope.length;
      const scopeB = b.shortcut.scope.length;
      if (scopeA !== scopeB) return scopeB - scopeA;

      // 权重优先级
      return b.parsed.priority - a.parsed.priority;
    });

    event.preventDefault();

    let captured = false;

    const setCaptured = () => {
      captured = true;
    };

    for (const { shortcut } of matches) {
      if (!shortcut.disabled) {
        shortcut.callback(event, setCaptured);
        if (!shortcut.noCapture || captured) {
          break; // 只要有一个匹配项不允许事件继续传播，就停止循环
        }
      }
    }
  }

  private _matchParsedKey(parsed: IParsedKey, pressedKey: string, event: KeyboardEvent): boolean {
    // 主键匹配
    if (parsed.key !== pressedKey) return false;

    // 修饰键匹配
    const { modifiers } = parsed;

    // 计算期望的修饰键状态
    const expectedCtrl = modifiers.ctrl || (modifiers.mod && !this._isApple);
    const expectedMeta = modifiers.meta || (modifiers.mod && this._isApple);
    const expectedAlt = modifiers.alt;
    const expectedShift = modifiers.shift;

    // 严格匹配：期望按下的必须按下，不期望按下的必须没按下
    return (
      expectedCtrl === event.ctrlKey &&
      expectedMeta === event.metaKey &&
      expectedAlt === event.altKey &&
      expectedShift === event.shiftKey
    );
  }

  // --------------------------------------------------------------------------
  // 工具方法
  // --------------------------------------------------------------------------

  private _parseKey(keyStr: string): IParsedKey {
    const parts = keyStr.toLowerCase().split('+');
    const modifierNames = ['ctrl', 'alt', 'shift', 'meta', 'mod'];

    // 最后一个非修饰键部分为主键
    const key = modifierNames.includes(parts[parts.length - 1]) ? '' : parts[parts.length - 1];

    const modifiers: ModifierState = {
      ctrl: parts.includes('ctrl'),
      alt: parts.includes('alt'),
      shift: parts.includes('shift'),
      meta: parts.includes('meta'),
      mod: parts.includes('mod')
    };

    // 计算优先级权重
    const priority = this.#calculatePriority(modifiers);

    return { key, modifiers, priority };
  }

  /**
   * 计算优先级权重
   * mod+shift+key = 3
   * mod+key = 2
   * shift+key = 1.5
   * alt+key = 1.25
   * key = 1
   */
  #calculatePriority(modifiers: ModifierState): number {
    let priority = 1;

    if (modifiers.mod || modifiers.ctrl || modifiers.meta) {
      priority += 1;
    }
    if (modifiers.shift) {
      priority += 0.5;
    }
    if (modifiers.alt) {
      priority += 0.25;
    }

    return priority;
  }

  #isInputElement(target: EventTarget | null): boolean {
    if (!target) return false;

    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    );
  }

  /** 添加事件监听器 */
  public addEventListener(eventName: EKeyboardEventName, callback: TKeyboardEvent[EKeyboardEventName]) {
    this.#eventBus.on(eventName, callback);
    return () => this.removeEventListener(eventName, callback);
  }

  /** 移除事件监听器 */
  public removeEventListener(eventName: EKeyboardEventName, callback: TKeyboardEvent[EKeyboardEventName]) {
    this.#eventBus.off(eventName, callback);
  }

  /** 触发事件 */
  public emitEvent(eventName: EKeyboardEventName) {
    this.#eventBus.emit(eventName);
  }

  /** 销毁键盘监听器 */
  destroy() {
    this.#removeListener();
    this.#shortcutMap.clear();
    this.#gs.keyboardStore.clearAllKeys();
  }
}
